/**
 * The bar above a view: which view, and how it is being looked at.
 *
 * Everything here changes what the rows are or how they are presented, and
 * every one of those changes reaches the grid the same way -- through the view
 * itself, or through the row query's signature. Nothing in this file talks to
 * the grid, and the grid has no idea any of it exists.
 *
 * The controls that are about the *rows* are shown for every view type that
 * draws rows -- see `drawsRows`, which is all of them bar the form. Sharing
 * sits beside them and is about the view rather than its rows, so it is
 * offered wherever there is a public page to share to.
 */
import type { Field, View } from '@/api/types'
import type { FieldPlacement, RowsView } from '../views/field-order'
import { useTranslation } from 'react-i18next'

import { SearchBox } from '@/shared/components/search-box'
import { readFieldOptions } from './field-options'
import { useFieldOptions } from './queries'
import { FieldOptionsMenu } from './field-options-menu'
import { DecorationsMenu } from '../decorations/decorations-menu'
import { CardCoverMenu } from './card-cover-menu'
import { DateFieldsMenu } from './date-fields-menu'
import { FilterMenu } from './filter-menu'
import { ListColumnsMenu } from './list-columns-menu'
import { StackByMenu } from './stack-by-menu'
import { GroupByMenu } from './group-by-menu'
import { RowHeightMenu } from './row-height-menu'
import { ShareMenu } from './share-menu'
import { ViewActions } from './view-actions'
import { SortMenu } from './sort-menu'
import { ViewSwitcher } from './view-switcher'

/** Whether this build draws this view's rows, and so offers controls over them. */
function drawsRows(view: View | undefined): view is RowsView {
  return (
    view?.type === 'grid' ||
    view?.type === 'gallery' ||
    view?.type === 'board' ||
    view?.type === 'list' ||
    view?.type === 'schedule' ||
    view?.type === 'gantt'
  )
}

export function ViewBar({
  views,
  view,
  fields,
  tableId,
  databaseId,
  search,
  onSearch,
}: {
  views: readonly View[]
  view: View | undefined
  fields: readonly Field[]
  tableId: number
  databaseId: number
  search: string
  onSearch: (term: string) => void
}) {
  const { t } = useTranslation()

  return (
    <>
      <ViewSwitcher
        views={views}
        current={view}
        tableId={tableId}
        databaseId={databaseId}
      />
      <ViewActions
        tableId={tableId}
        databaseId={databaseId}
        view={view}
        views={views}
        fields={fields}
      />
      <div className="ml-auto flex min-w-0 items-center gap-2">
        {drawsRows(view) && (
          <>
            {/*
              The presentation controls, and the only part of the bar allowed to
              run out of room. A view type can offer eight of them with labels
              that grow a count, which on a narrow window used to push the search
              box off the end of the bar; here they scroll among themselves and
              everything to the right of them keeps its place.
            */}
            <div className="flex min-w-0 items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <FilterMenu tableId={tableId} view={view} fields={fields} />
              <SortMenu tableId={tableId} view={view} fields={fields} />
              {/* Grouping and row height are the grid's alone: the other row
                  endpoints take no `group_by`, and a card's height comes from the
                  fields the view shows rather than from a setting. */}
              {view.type === 'grid' && (
                <>
                  <GroupByMenu tableId={tableId} view={view} fields={fields} />
                  <RowHeightMenu tableId={tableId} view={view} />
                </>
              )}
              {/* Which columns the view is *about*, which every type but the
                  grid has to be told. Beside the controls over the rows rather
                  than in the view's own menu, because getting one wrong is what
                  leaves the view drawing nothing. */}
              {(view.type === 'schedule' || view.type === 'gantt') && (
                <DateFieldsMenu tableId={tableId} view={view} fields={fields} />
              )}
              {view.type === 'board' && (
                <StackByMenu tableId={tableId} view={view} fields={fields} />
              )}
              {view.type === 'list' && (
                <ListColumnsMenu tableId={tableId} view={view} fields={fields} />
              )}
              {(view.type === 'board' || view.type === 'gallery') && (
                <CardCoverMenu tableId={tableId} view={view} fields={fields} />
              )}
              <DecorationsMenu tableId={tableId} view={view} fields={fields} />
              <FieldOptionsControl view={view} fields={fields} />
            </div>
            <div className="shrink-0">
              <SearchBox
                term={search}
                onSearch={onSearch}
                label={t('view.search')}
                // `className` replaces the box's own geometry rather than
                // adding to it, so the size is restated alongside the surface.
                className="h-8 w-40 pr-7 pl-7 lg:w-56 bg-background on-surface"
              />
            </div>
          </>
        )}
        {/* Outside the grid block, because sharing is about the view rather
            than about its rows. It draws nothing for a view type this build has
            no public page for, which is every type but the form so far. */}
        {view !== undefined && <ShareMenu tableId={tableId} view={view} />}
      </div>
    </>
  )
}

/**
 * Which fields the view shows, and in what order.
 *
 * A component of its own so that reading the field options is a hook rather
 * than a condition, and because there is nothing to read until the view is
 * known to be one that has them. Whatever is drawing the rows is subscribed to
 * the same query, so this costs a second reader and no second request.
 */
function FieldOptionsControl({
  view,
  fields,
}: {
  view: RowsView
  fields: readonly Field[]
}) {
  const options = useFieldOptions(view.id)

  // Nothing until the view has said how it shows its fields. An empty answer
  // and an answer that has not arrived look the same to `readFieldOptions`, so
  // a menu drawn before it lands would report every column as shown -- and
  // would then write a change on top of an answer still in flight.
  if (options.data === undefined) return null

  return (
    <FieldOptionsMenu
      viewId={view.id}
      fields={fields}
      options={readFieldOptions<FieldPlacement>(options.data)}
    />
  )
}
